import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import './Home.css';

function Home() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showGuide, setShowGuide] = useState(false);

  const categories = {
    OLL: {
      title: "OLL Practice",
      description: "Orient the Last Layer",
      modes: [
        {
          name: "Recognition + Execution",
          path: "/oll-recognition",
          description: "Practice recognizing OLL cases after F2L insertion"
        },
        {
          name: "Random Practice",
          path: "/random-oll",
          description: "Practice executing random OLL algorithms"
        },
        {
          name: "Single Case Practice",
          path: "/single-oll",
          description: "Practice specific OLL cases"
        }
      ]
    },
    PLL: {
      title: "PLL Practice",
      description: "Permute the Last Layer",
      modes: [
        {
          name: "Recognition + Execution",
          path: "/pll-recognition",
          description: "Practice recognizing PLL cases after F2L insertion"
        },
        {
          name: "Random Practice",
          path: "/random-pll",
          description: "Practice executing random PLL algorithms"
        },
        {
          name: "Single Case Practice",
          path: "/single-pll",
          description: "Practice specific PLL cases"
        }
      ]
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="home">
      <div className="user-section">
        <span className="welcome-text">Welcome, {user?.email}</span>
        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          className="logout-button"
        >
          Logout
        </Button>
      </div>

      <div className="app-header">
        <h1>
          Last Layer Trainer{' '}
          <span className="beta-tag">(beta)</span>
        </h1>
        <Button
          variant="outlined"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setShowGuide(true)}
          className="help-button"
        >
          How to Get Started
        </Button>
      </div>
      
      <div className="practice-menu">
        {Object.entries(categories).map(([key, category]) => (
          <div className="category-section" key={key}>
            <h2>{category.title}</h2>
            <p className="category-description">{category.description}</p>
            <div className="mode-grid">
              {category.modes.map((mode) => (
                <Link to={mode.path} className="menu-item" key={mode.path}>
                  <h3>{mode.name}</h3>
                  <p className="mode-description">{mode.description}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
        
        <Link to="/slowest-algorithms" className="menu-item analysis-item">
          <h3>Practice Analysis</h3>
          <p className="mode-description">View your recent algorithm practice statistics</p>
        </Link>

        <Link to="/release-notes" className="menu-item release-notes-item">
          <h3>Release Notes</h3>
          <p className="mode-description">View latest updates and improvements</p>
        </Link>
      </div>

      <Dialog 
        open={showGuide} 
        onClose={() => setShowGuide(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          How to Get Started
        </DialogTitle>
        <DialogContent>
          <div className="guide-content">
            <div className="guide-step">
              <h3>1. Begin with Random Practice</h3>
              <p>Start by practicing random OLL and/or PLL algorithms. This helps establish baseline data for your solves.</p>
            </div>
            
            <div className="guide-step">
              <h3>2. Check Your Progress</h3>
              <p>Navigate to the Practice Analysis page to see which algorithms still need execution data and which ones need improvement.</p>
            </div>
            
            <div className="guide-step">
              <h3>3. Targeted Practice</h3>
              <p>Use the "Practice These Algorithms" button in the Practice Analysis page (or various practice modes) to focus on specific algorithms that need attention.</p>
            </div>
            
            <div className="guide-tip">
              <p><strong>Tip:</strong> Regular practice with a mix of execution and recognition + execution sessions will help you prepare for real solve scenarios!</p>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGuide(false)} color="primary">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Home; 